import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  MessageCircle,
  X,
  Mail,
  Linkedin,
  Github,
  Instagram,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileDown,
} from "lucide-react";

import { person } from "../data/profile";
import { cn } from "../lib/utils";
import { useResumeUrl } from "../lib/resumeStore";

/**
 * ContactDock — a persistent way to reach out from anywhere on the page.
 *
 * The contact section is at the very bottom of a ~18,000px document. Anyone
 * who decides to get in touch while reading the work section shouldn't have
 * to scroll past four more sections to do it.
 *
 * Two states: a collapsed FAB, and an expanded panel with the direct
 * channels plus a three-field form that posts to the same endpoint as the
 * main contact form.
 */

const buildChannels = (resumeUrl) => [
  {
    key: "linkedin",
    label: "LinkedIn",
    sub: "in/soham-dang",
    href: person.social.linkedin,
    Icon: Linkedin,
    tone: "text-sky-300 border-sky-400/25 bg-sky-400/[0.07]",
  },
  {
    key: "instagram",
    label: "Instagram",
    sub: "@soham_.31",
    href: person.social.instagram,
    Icon: Instagram,
    tone: "text-pink-300 border-pink-400/25 bg-pink-400/[0.07]",
  },
  {
    key: "github",
    label: "GitHub",
    sub: "Soham3131",
    href: person.social.github,
    Icon: Github,
    tone: "text-slate-200 border-white/15 bg-white/[0.05]",
  },
  {
    key: "email",
    label: "Email",
    sub: person.email,
    href: `mailto:${person.email}`,
    Icon: Mail,
    tone: "text-brand-300 border-brand-400/25 bg-brand-400/[0.07]",
  },
  {
    key: "resume",
    label: "Résumé",
    sub: "PDF download",
    href: resumeUrl,
    download: "Soham-Dang-Resume.pdf",
    Icon: FileDown,
    tone: "text-violet-300 border-violet-400/25 bg-violet-400/[0.07]",
  },
];

export default function ContactDock() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("links");
  const [form, setForm] = useState({ name: "", email: "", message: "", hp: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const panelRef = useRef(null);
  const resumeUrl = useResumeUrl();
  const CHANNELS = buildChannels(resumeUrl);

  // Close on Escape, and on a click outside the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", text: "All three fields are required." });
      return;
    }
    if (form.hp) return; // honeypot

    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, hp: undefined, source: "Contact Dock" }),
      });
      if (res.ok) {
        setStatus({ type: "success", text: "Sent — I'll reply shortly." });
        setForm({ name: "", email: "", message: "", hp: "" });
      } else {
        setStatus({ type: "error", text: "Server declined. Try email instead." });
      }
    } catch {
      setStatus({ type: "error", text: "Network error. Email works as a fallback." });
    } finally {
      setBusy(false);
      window.setTimeout(() => setStatus(null), 6000);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[80] sm:bottom-6 sm:right-6">
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-label="Contact Soham Dang"
            className="panel absolute bottom-16 right-0 w-[min(21rem,calc(100vw-2rem))] origin-bottom-right overflow-hidden rounded-3xl sm:bottom-[4.25rem] sm:w-[22rem]"
            style={{ backdropFilter: "blur(20px)" }}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-5 py-4">
              <div>
                <p className="font-display text-sm font-semibold text-slate-50">
                  Get in touch
                </p>
                <p className="mt-0.5 text-[0.7rem] text-slate-500">
                  Usually replies within a day
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-400/25 bg-brand-500/10 px-2 py-1 font-mono text-[0.52rem] uppercase tracking-label text-brand-300">
                <span className="h-1 w-1 animate-pulse rounded-full bg-brand-400" />
                Open to work
              </span>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-3 pt-3">
              {[
                ["links", "Channels"],
                ["form", "Send a message"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={cn(
                    "flex-1 rounded-xl px-3 py-2 text-[0.72rem] font-medium transition-colors",
                    tab === id
                      ? "bg-white/[0.07] text-slate-100"
                      : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "links" ? (
              <ul className="grid grid-cols-2 gap-2 p-3">
                {CHANNELS.map(({ key, label, sub, href, Icon, tone, download }) => (
                  <li key={key}>
                    <a
                      href={href}
                      {...(download
                        ? { download }
                        : href.startsWith("http")
                        ? { target: "_blank", rel: "noopener noreferrer me" }
                        : {})}
                      className="flex flex-col gap-2 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 transition-colors hover:border-white/15 hover:bg-white/[0.05]"
                    >
                      <span
                        className={cn(
                          "grid h-8 w-8 place-items-center rounded-lg border",
                          tone
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[0.78rem] font-medium text-slate-200">
                          {label}
                        </span>
                        <span className="block truncate font-mono text-[0.58rem] text-slate-600">
                          {sub}
                        </span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <form onSubmit={submit} className="space-y-2.5 p-3" noValidate>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your name"
                  autoComplete="name"
                  className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 text-[0.82rem] text-slate-100 placeholder-slate-600 outline-none transition-colors focus:border-brand-400/50"
                />
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 text-[0.82rem] text-slate-100 placeholder-slate-600 outline-none transition-colors focus:border-brand-400/50"
                />
                <textarea
                  name="message"
                  rows={3}
                  value={form.message}
                  onChange={onChange}
                  placeholder="What are you building?"
                  className="w-full resize-y rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 text-[0.82rem] text-slate-100 placeholder-slate-600 outline-none transition-colors focus:border-brand-400/50"
                />
                <input
                  name="hp"
                  value={form.hp}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="sr-only-seo"
                />

                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary w-full py-2.5 text-[0.82rem] disabled:opacity-60"
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" />
                      Send
                    </>
                  )}
                </button>

                {status && (
                  <p
                    role="status"
                    className={cn(
                      "flex items-start gap-2 rounded-xl border px-3 py-2 text-[0.72rem]",
                      status.type === "success"
                        ? "border-brand-400/30 bg-brand-500/[0.08] text-brand-200"
                        : "border-rose-400/30 bg-rose-500/[0.08] text-rose-200"
                    )}
                  >
                    {status.type === "success" ? (
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    )}
                    {status.text}
                  </p>
                )}
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close contact panel" : "Open contact panel"}
        className="group relative grid h-14 w-14 place-items-center rounded-full shadow-glow-lg transition-transform duration-300 ease-out2 hover:scale-105 active:scale-95"
      >
        <span className="absolute inset-0 rounded-full bg-brand-gradient" />
        {!open && (
          <span className="absolute inset-0 animate-pulse-ring rounded-full bg-brand-400/50" />
        )}
        <span className="relative text-ink-950">
          {open ? (
            <X className="h-5 w-5" />
          ) : (
            <MessageCircle className="h-5 w-5" />
          )}
        </span>
      </button>
    </div>
  );
}
